/**
 * Contratos de onda da Batalha Infinita.
 *
 * Cada onda entra com um contrato opcional visível no HUD — algo para
 * perseguir nos próximos 30-90 segundos, além de "não morra". Falhar não custa
 * nada: só deixa de pagar o bônus. Punir uma criança por um objetivo opcional
 * produz frustração, não engajamento.
 *
 * Este módulo é lógica pura: um catálogo, um sorteador e um redutor de eventos.
 * Quem observa o jogo e emite os eventos é o store (`useInfiniteRun`).
 */

import { seededRandom, weightedSample } from "./rng";

export type ContractId =
  | "no_damage"
  | "no_items"
  | "speedrun"
  | "combo"
  | "priority_target"
  | "demolisher"
  | "long_range"
  | "close_quarters"
  | "full_health"
  | "firewalker";

/**
 * Camada do contrato.
 *
 * Sem isto o modo escalava contratos do único jeito que eu mesmo critiquei na
 * curva original: aumentando o número da meta. Os avançados pedem outra
 * postura — chegar perto, terminar inteiro, atravessar o fogo — e vão ganhando
 * peso conforme a corrida avança, até dominarem o sorteio.
 */
export type ContractTier = "basic" | "advanced";

export type ContractStatus = "active" | "completed" | "failed";

export type EnemyKind = "normal" | "heavy" | "fast" | "sniper" | "boss";

export interface ContractDef {
  id: ContractId;
  icon: string;
  tier: ContractTier;
  /** Onda mínima — os primeiros minutos são para aprender a atirar. */
  minWave: number;
  /** Peso relativo no sorteio. */
  weight: number;
  /** Alvo em função da onda (segundos, quantidade...). `undefined` = binário. */
  targetFor?: (wave: number) => number;
  /** Recompensa em chips, em função da onda. */
  rewardFor: (wave: number) => number;
}

export const CONTRACT_DEFS: readonly ContractDef[] = Object.freeze([
  {
    id: "no_damage",
    tier: "basic",
    icon: "🛡️",
    minWave: 3,
    weight: 100,
    rewardFor: (wave) => 60 + wave * 8,
  },
  {
    id: "no_items",
    tier: "basic",
    icon: "🚫",
    minWave: 4,
    weight: 70,
    rewardFor: (wave) => 45 + wave * 6,
  },
  {
    id: "speedrun",
    tier: "basic",
    icon: "⏱️",
    minWave: 3,
    weight: 100,
    // Sobe com a onda porque a onda tem mais inimigos; nunca abaixo de 30 s.
    targetFor: (wave) => Math.max(30, Math.round(34 + Math.min(wave, 20) * 1.6)),
    rewardFor: (wave) => 55 + wave * 7,
  },
  {
    id: "combo",
    tier: "basic",
    icon: "🔥",
    minWave: 5,
    weight: 80,
    targetFor: (wave) => (wave >= 12 ? 4 : 3),
    rewardFor: (wave) => 70 + wave * 8,
  },
  {
    id: "priority_target",
    tier: "basic",
    icon: "🎯",
    minWave: 6,
    weight: 85,
    rewardFor: (wave) => 65 + wave * 7,
  },
  {
    id: "demolisher",
    tier: "basic",
    icon: "🧱",
    minWave: 4,
    weight: 60,
    targetFor: (wave) => (wave >= 10 ? 8 : 5),
    rewardFor: (wave) => 40 + wave * 5,
  },
  {
    id: "long_range",
    tier: "basic",
    icon: "🔭",
    minWave: 7,
    weight: 70,
    targetFor: (wave) => (wave >= 14 ? 4 : 3),
    rewardFor: (wave) => 60 + wave * 7,
  },

  // ---- Avançados: pedem outra postura, não um número maior ----
  {
    id: "close_quarters",
    tier: "advanced",
    icon: "🤜",
    minWave: 9,
    weight: 80,
    targetFor: (wave) => (wave >= 18 ? 4 : 3),
    rewardFor: (wave) => 85 + wave * 9,
  },
  {
    id: "full_health",
    tier: "advanced",
    icon: "💚",
    minWave: 11,
    weight: 70,
    // Sobe com a onda: terminar acima de 60% na 12 é fácil, na 30 não é.
    targetFor: (wave) => Math.min(85, 55 + Math.floor(wave / 4) * 5),
    rewardFor: (wave) => 90 + wave * 9,
  },
  {
    id: "firewalker",
    tier: "advanced",
    icon: "🔥",
    minWave: 13,
    weight: 65,
    targetFor: (wave) => (wave >= 22 ? 3 : 2),
    rewardFor: (wave) => 120 + wave * 11,
  },
]);

const DEF_BY_ID = new Map(CONTRACT_DEFS.map((def) => [def.id, def]));

/** Janela do combo, em ms. */
export const COMBO_WINDOW_MS = 6000;
/** Distância a partir da qual um abate conta como "longa distância". */
export const LONG_RANGE_DISTANCE = 22;
/** Distância abaixo da qual um abate conta como corpo a corpo. */
export const CLOSE_QUARTERS_DISTANCE = 9;

/** Contrato ativo numa onda, com seu progresso. */
export interface WaveContract {
  id: ContractId;
  icon: string;
  /** Alvo numérico (segundos ou quantidade). 0 em contratos binários. */
  target: number;
  current: number;
  status: ContractStatus;
  reward: number;
  /** Só em `priority_target`: qual tipo de inimigo tem de morrer primeiro. */
  targetKind?: EnemyKind;
  /** Timestamps de abates recentes, para a janela do combo. */
  killTimestamps?: number[];
}

/**
 * Sorteia o contrato da onda.
 *
 * `availableKinds` vem da composição real da onda: não faz sentido pedir "mate
 * o atirador primeiro" numa onda sem atiradores. Retorna null quando a onda é
 * cedo demais ou o sorteio caiu fora — nem toda onda precisa de contrato, e o
 * silêncio ocasional dá ritmo.
 */
export function rollWaveContract(
  wave: number,
  availableKinds: readonly EnemyKind[],
  seed: number,
): WaveContract | null {
  const random = seededRandom(seed);

  // Uma em cada cinco ondas fica sem contrato — respiro.
  if (random() < 0.2) return null;

  const priorityKinds = availableKinds.filter((kind) => kind !== "normal");

  const eligible = CONTRACT_DEFS.filter((def) => {
    if (wave < def.minWave) return false;
    if (def.id === "priority_target" && priorityKinds.length === 0) return false;
    return true;
  });
  if (eligible.length === 0) return null;

  // Os avançados começam raros e viram o normal por volta da onda 25. É assim
  // que o desafio muda de natureza junto com o poder do tanque, em vez de só
  // pedir o mesmo com um número maior.
  const advancedBoost = Math.min(2.6, 0.35 + Math.max(0, wave - 9) * 0.13);
  const [def] = weightedSample(
    eligible,
    (d) => (d.tier === "advanced" ? d.weight * advancedBoost : d.weight),
    1,
    random,
  );
  if (!def) return null;

  const contract: WaveContract = {
    id: def.id,
    icon: def.icon,
    target: def.targetFor ? def.targetFor(wave) : 0,
    current: 0,
    status: "active",
    reward: def.rewardFor(wave),
  };

  if (def.id === "priority_target") {
    contract.targetKind = priorityKinds[Math.floor(random() * priorityKinds.length)];
  }
  if (def.id === "combo") {
    contract.killTimestamps = [];
  }

  return contract;
}

// ============================================
// EVENTOS
// ============================================

export type ContractEvent =
  | { type: "playerDamaged" }
  | { type: "itemUsed" }
  | { type: "obstacleDestroyed" }
  | {
      type: "enemyKilled";
      kind: EnemyKind;
      distance: number;
      at: number;
      /** O tanque estava em chamas no momento do abate. */
      burning?: boolean;
    }
  | {
      type: "waveCleared";
      elapsedSeconds: number;
      /** Vida do jogador ao fim da onda, em porcentagem. */
      healthPercent?: number;
    };

/**
 * Aplica um evento ao contrato e devolve o novo estado.
 *
 * Puro e imutável: nunca muta o contrato recebido, e um contrato já resolvido
 * (completo ou falho) ignora tudo — o resultado da onda não pode ser revertido
 * por um evento tardio.
 */
export function applyContractEvent(
  contract: WaveContract,
  event: ContractEvent,
): WaveContract {
  if (contract.status !== "active") return contract;

  switch (contract.id) {
    case "no_damage":
      return event.type === "playerDamaged" ? { ...contract, status: "failed" }
        : event.type === "waveCleared" ? { ...contract, status: "completed" }
        : contract;

    case "no_items":
      return event.type === "itemUsed" ? { ...contract, status: "failed" }
        : event.type === "waveCleared" ? { ...contract, status: "completed" }
        : contract;

    case "speedrun":
      if (event.type !== "waveCleared") return contract;
      return {
        ...contract,
        current: Math.round(event.elapsedSeconds),
        status: event.elapsedSeconds <= contract.target ? "completed" : "failed",
      };

    case "demolisher": {
      if (event.type === "obstacleDestroyed") {
        const current = contract.current + 1;
        return { ...contract, current, status: current >= contract.target ? "completed" : "active" };
      }
      // Não completou até o fim da onda: falhou.
      if (event.type === "waveCleared") return { ...contract, status: "failed" };
      return contract;
    }

    case "long_range": {
      if (event.type === "enemyKilled" && event.distance >= LONG_RANGE_DISTANCE) {
        const current = contract.current + 1;
        return { ...contract, current, status: current >= contract.target ? "completed" : "active" };
      }
      if (event.type === "waveCleared") return { ...contract, status: "failed" };
      return contract;
    }

    case "combo": {
      if (event.type === "enemyKilled") {
        const window = (contract.killTimestamps ?? []).filter(
          (at) => event.at - at < COMBO_WINDOW_MS,
        );
        window.push(event.at);
        const best = Math.max(contract.current, window.length);
        return {
          ...contract,
          killTimestamps: window,
          current: best,
          status: best >= contract.target ? "completed" : "active",
        };
      }
      if (event.type === "waveCleared") return { ...contract, status: "failed" };
      return contract;
    }

    case "close_quarters": {
      if (event.type === "enemyKilled" && event.distance <= CLOSE_QUARTERS_DISTANCE) {
        const current = contract.current + 1;
        return { ...contract, current, status: current >= contract.target ? "completed" : "active" };
      }
      if (event.type === "waveCleared") return { ...contract, status: "failed" };
      return contract;
    }

    case "firewalker": {
      // Só conta abate feito EM CHAMAS: o contrato pede que o jogador aceite o
      // dano da lava em troca do bônus, em vez de contorná-la.
      if (event.type === "enemyKilled" && event.burning) {
        const current = contract.current + 1;
        return { ...contract, current, status: current >= contract.target ? "completed" : "active" };
      }
      if (event.type === "waveCleared") return { ...contract, status: "failed" };
      return contract;
    }

    case "full_health": {
      if (event.type !== "waveCleared") return contract;
      const percent = Math.round(event.healthPercent ?? 0);
      return {
        ...contract,
        current: percent,
        status: percent >= contract.target ? "completed" : "failed",
      };
    }

    case "priority_target": {
      if (event.type !== "enemyKilled") return contract;
      // O primeiro abate decide: se foi o alvo, cumpriu; se não, falhou.
      return {
        ...contract,
        current: 1,
        status: event.kind === contract.targetKind ? "completed" : "failed",
      };
    }

    default:
      return contract;
  }
}

export function getContractDef(id: ContractId): ContractDef | undefined {
  return DEF_BY_ID.get(id);
}

/** Texto de progresso para o HUD ("3/5", "42s / 50s"). Vazio se binário. */
export function formatContractProgress(contract: WaveContract): string {
  if (contract.target <= 0) return "";
  if (contract.id === "speedrun") return `${contract.target}s`;
  if (contract.id === "full_health") return `${contract.target}%`;
  return `${Math.min(contract.current, contract.target)}/${contract.target}`;
}
